import React, { useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { API_BASE_URL } from '../../config';
import { useSelectStyles } from '../../utils/selectStyles';

const GIInterventionPointPicker = ({
  value,
  onChange,
  fetchWithAuth,
  className = '',
  placeholder = 'Rechercher un point d\'intervention...'
}) => {
  const selectStyles = useSelectStyles();
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const controllerRef = useRef(null);

  const fetchByName = async (term) => {
    if (!term || term.trim().length < 3) {
      setOptions([]);
      return;
    }
    try {
      controllerRef.current?.abort?.();
      controllerRef.current = new AbortController();
      setLoading(true);
      const resp = await fetchWithAuth(`${API_BASE_URL}/api/dwh/gi/planning/intervention-points?name=${encodeURIComponent(term)}&limit=20`, {
        signal: controllerRef.current.signal
      });
      if (!resp.ok) return;
      const data = await resp.json();
      const opts = (data.interventionPoints || []).map(ip => ({
        value: ip.Id,
        label: `${ip.Name}${ip.AddressCity ? ` - ${ip.AddressCity}` : ''}`,
        meta: ip
      }));
      setOptions(opts);
    } finally {
      setLoading(false);
    }
  };

  const fetchByGeo = async () => {
    if (!navigator.geolocation) return;
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(async (pos) => {
        try {
          setLoading(true);
          const { latitude, longitude } = pos.coords;
          const resp = await fetchWithAuth(`${API_BASE_URL}/api/dwh/gi/planning/intervention-points?lat=${latitude}&long=${longitude}&limit=20`);
          if (!resp.ok) return resolve();
          const data = await resp.json();
          const opts = (data.interventionPoints || []).map(ip => ({
            value: ip.Id,
            label: `${ip.Name}${ip.AddressCity ? ` - ${ip.AddressCity}` : ''}`,
            meta: ip
          }));
          setOptions(opts);
        } finally {
          setLoading(false);
          resolve();
        }
      }, () => resolve(), { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 });
    });
  };

  // Debounce search
  useEffect(() => {
    const id = setTimeout(() => fetchByName(inputValue), 300);
    return () => clearTimeout(id);
  }, [inputValue]);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    const found = options.find(o => o.value === value.value);
    return found || value;
  }, [value, options]);

  return (
    <div className={className}>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <Select
            value={selectedOption}
            onChange={onChange}
            onInputChange={setInputValue}
            inputValue={inputValue}
            options={options}
            isClearable
            isSearchable
            placeholder={placeholder}
            noOptionsMessage={() => inputValue.trim().length < 3 ? 'Tapez au moins 3 caractères' : (loading ? 'Chargement...' : 'Aucun résultat')}
            styles={selectStyles}
            className="text-sm"
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
          />
        </div>
        <button
          type="button"
          onClick={fetchByGeo}
          className="px-2 py-2 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700"
          title="Utiliser ma position"
        >
          GPS
        </button>
      </div>
    </div>
  );
};

export default GIInterventionPointPicker;


